/**
 * Integrytas Group - Messaging & Task Automation (MTA)
 * Page: MTA.TASKS
 *
 * Personal task inbox: the assignable to-do items generated by automation rules
 * (mta_task). Users see their own tasks (RLS), update status, and follow the
 * link back to the source record/screen.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { RowActionsMenu } from '../../components/common/RowActionsMenu';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';

type TaskStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';

// Task status (CHECK enum) → localized label + badge variant (§17.B11).
const STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  OPEN:        { labelKey: 'mta_tasks.status.OPEN',        label: 'Open',        variant: 'success' },
  IN_PROGRESS: { labelKey: 'mta_tasks.status.IN_PROGRESS', label: 'In progress', variant: 'warning' },
  DONE:        { labelKey: 'mta_tasks.status.DONE',        label: 'Done',        variant: 'success' },
  CANCELLED:   { labelKey: 'mta_tasks.status.CANCELLED',   label: 'Cancelled',   variant: 'neutral' },
};
interface TaskRow {
  mta_task_id: number; title: string; body: string | null;
  entity_name: string | null; record_id: string | null;
  status: TaskStatus; due_date: string | null; created_at: string;
}

const TASK_GRID_COLS: GridColumnDef[] = [
  { key: 'title',       header: 'Title',  defaultWidth: 280 },
  { key: 'status',      header: 'Status', defaultWidth: 130 },
  { key: 'body',        header: 'Detail', defaultWidth: 280 },
  { key: 'due_date',    header: 'Due',    defaultWidth: 120 },
  { key: 'source',      header: 'Source', defaultWidth: 180 },
];

export interface MtaTasksPageProps { session: AuthSession }

export function MtaTasksPage({ session }: MtaTasksPageProps) {
  return (
    <PersonalizationProvider pageCode="MTA.TASKS" session={session}>
      <MtaTasksPageInner session={session} />
    </PersonalizationProvider>
  );
}

function MtaTasksPageInner({ session }: MtaTasksPageProps) {
  const { t } = useTranslation();
  const myId = session.effective_user.user_id;
  const actor = getForensicActorId(session);
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [filter, setFilter] = useState<'OPEN' | 'ALL'>('OPEN');
  const [text, setText] = useState('');
  // §17.B5: per-column multi-value facets (status of the loaded tasks).
  const [facets, setFacets] = useState<{ status: string[] }>({ status: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detail = useExpandedRows<number>();

  // §17.B13 related-children (expand/contract). A task's only relation is the
  // source record (entity_name#record_id), for which no resolved display name is
  // loaded — showing the raw table/id would breach §17.B4 — so the panel is inert.
  // TODO: wire relations (resolve the source record's display name).
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      let q = apiDb.from('mta_task')
        .select('mta_task_id, title, body, entity_name, record_id, status, due_date, created_at')
        .eq('assignee_user_id', myId);
      if (filter === 'OPEN') q = q.in('status', ['OPEN', 'IN_PROGRESS']);
      const { data, error: e } = await q.order('created_at', { ascending: false });
      if (e) throw e;
      setRows((data ?? []) as TaskRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  // reload is re-created each render; reload only when the filter changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [filter]);

  // Distinct status facet options derived from the loaded rows (§17.B5).
  const statusOptions = useMemo(
    () => [...new Set(rows.map(r => r.status).filter(Boolean))].sort().map(v => ({ value: v, label: v })),
    [rows],
  );
  const anyFilterActive = facets.status.length > 0 || text.trim().length > 0;
  const clearAll = () => { setText(''); setFacets({ status: [] }); };

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    const matchesFacets = (task: TaskRow) => facets.status.length === 0 || facets.status.includes(task.status);
    return rows.filter(task =>
      (!q || task.title.toLowerCase().includes(q) || (task.body ?? '').toLowerCase().includes(q) || (task.entity_name ?? '').toLowerCase().includes(q))
      && matchesFacets(task));
  }, [rows, text, facets]);

  const setStatus = async (id: number, status: TaskStatus) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await apiDb.from('mta_task').update({ status, modified_by: actor }).eq('mta_task_id', id);
      if (e) throw e;
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('mta_tasks.heading', 'My Tasks')}
          description={t('mta_tasks.list.description', 'To-do items assigned to you by automation rules, with a link back to the source record.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('mta_tasks.refresh', 'Refresh')}
            </button>
          }
        />
        {error && (
          <ErrorBanner
            message={t('mta_tasks.error_banner', "Couldn't complete the last tasks operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <select value={filter} onChange={e => setFilter(e.target.value as 'OPEN' | 'ALL')} className="h-8 rounded-csm-control border border-csm-border px-2 text-csm-sm">
              <option value="OPEN">{t('mta_tasks.filter_open', 'Open')}</option><option value="ALL">{t('mta_tasks.filter_all', 'All')}</option>
            </select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={t('mta_tasks.filter_placeholder', 'Filter tasks…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('mta_tasks.col.status', 'Status')} options={statusOptions} selected={facets.status} onChange={v => setFacets({ status: v })} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('mta_tasks.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={TASK_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('mta_tasks.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('mta_tasks.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('mta_tasks.col_actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(task => (
                  <Fragment key={task.mta_task_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(task.mta_task_id)} hasChildren={hasRelated(task.mta_task_id)} onToggle={() => detail.toggle(task.mta_task_id)} expandLabel={t('mta_tasks.related.expand', 'Show related records')} collapseLabel={t('mta_tasks.related.collapse', 'Hide related')} tooltip={t('mta_tasks.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 align-top text-csm-sm"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'title'  && <span className="font-semibold text-csm-ink">{task.title}</span>}
                        {col.key === 'status' && (() => { const meta = STATUS_META[task.status]; return <StatusBadge variant={meta?.variant ?? 'neutral'} label={meta ? t(meta.labelKey, meta.label) : task.status} />; })()}
                        {col.key === 'body'   && <span className="text-csm-sm text-csm-ink-soft">{task.body}</span>}
                        {col.key === 'due_date' && <span className="text-csm-2xs text-csm-watermark">{task.due_date ?? '—'}</span>}
                        {col.key === 'source' && (task.entity_name ? <span className="font-mono text-csm-2xs text-csm-watermark">{task.entity_name}#{task.record_id}</span> : '—')}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right align-top whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => void setStatus(task.mta_task_id, 'DONE')} disabled={task.status === 'DONE'} className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover disabled:opacity-50">{t('mta_tasks.mark_done', 'Mark Done')}</button>
                        <RowActionsMenu
                          label={t('common.row_actions', 'Row actions')}
                          actions={[
                            { key: 'start',  label: t('mta_tasks.start', 'Start'),   onSelect: () => void setStatus(task.mta_task_id, 'IN_PROGRESS'), disabled: task.status === 'IN_PROGRESS' || task.status === 'DONE' },
                            { key: 'cancel', label: t('mta_tasks.cancel', 'Cancel'), onSelect: () => void setStatus(task.mta_task_id, 'CANCELLED'),   disabled: task.status === 'CANCELLED' || task.status === 'DONE' },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                  {detail.isOpen(task.mta_task_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3">
                        <RelatedChildrenPanel groups={buildRelatedGroups(task.mta_task_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="px-2 py-8 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'all-clear'} title={t('mta_tasks.empty', 'No tasks.')} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
